import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Clock,
  Check,
  AlertTriangle,
  Plus,
  X,
  Loader2,
  CalendarDays,
  FolderKanban,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate, formatDateShort } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";

interface Reminder {
  id: number;
  title: string;
  description: string;
  due_date: string;
  reminder_type: string;
  is_completed: boolean;
  project_id: number;
  project_title?: string;
  assigned_to_name?: string;
  created_at: string;
}

interface RemindersResponse {
  data: Reminder[];
}

interface Project {
  id: number;
  title: string;
}

interface ProjectsResponse {
  data: Project[];
  pagination: { page: number; totalPages: number; total: number };
}

type FilterTab = "upcoming" | "overdue" | "completed";

const REMINDER_TYPES: Record<string, string> = {
  deadline: "Son Tarih",
  follow_up: "Takip",
  meeting: "Toplantı",
  document: "Belge",
  payment: "Ödeme",
  other: "Diğer",
};

const initialForm = {
  project_id: "",
  title: "",
  description: "",
  due_date: "",
  reminder_type: "follow_up",
};

export function RemindersPage() {
  const [activeFilter, setActiveFilter] = useState<FilterTab>("upcoming");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [completingId, setCompletingId] = useState<number | null>(null);
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useFetch<RemindersResponse>(
    `/reminders?filter=${activeFilter}`,
    [activeFilter]
  );

  const { data: projectsData } = useFetch<ProjectsResponse>(
    "/projects?limit=200&status=active"
  );

  function handleFormChange(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.due_date) return;
    setSubmitting(true);
    try {
      const payload = {
        project_id: form.project_id ? Number(form.project_id) : undefined,
        title: form.title,
        description: form.description || undefined,
        due_date: form.due_date,
        reminder_type: form.reminder_type,
      };
      await adminFetch("/reminders", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setForm(initialForm);
      setShowModal(false);
      refetch();
    } catch {
      // Error handled
    } finally {
      setSubmitting(false);
    }
  }

  async function handleComplete(id: number) {
    setCompletingId(id);
    try {
      await adminFetch(`/reminders/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_completed: true }),
      });
      refetch();
    } catch {
      // Error handled
    } finally {
      setCompletingId(null);
    }
  }

  const filterTabs: {
    key: FilterTab;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }[] = [
    { key: "upcoming", label: "Yaklaşan", icon: Clock },
    { key: "overdue", label: "Gecikmiş", icon: AlertTriangle },
    { key: "completed", label: "Tamamlanan", icon: Check },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors",
                activeFilter === tab.key
                  ? "border-accent/30 bg-accent/10 text-accent"
                  : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <Plus className="h-4 w-4" />
          Yeni Hatırlatıcı
        </button>
      </div>

      {/* Reminders list */}
      {loading ? (
        <PageLoading />
      ) : error ? (
        <ErrorMessage message={error} onRetry={refetch} />
      ) : !data || data.data.length === 0 ? (
        <div className="rounded-xl border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
          {activeFilter === "upcoming" && "Yaklaşan hatırlatıcı bulunmuyor."}
          {activeFilter === "overdue" && "Gecikmiş hatırlatıcı bulunmuyor."}
          {activeFilter === "completed" &&
            "Tamamlanan hatırlatıcı bulunmuyor."}
        </div>
      ) : (
        <div className="space-y-3">
          {data.data.map((reminder) => {
            const isOverdue =
              !reminder.is_completed &&
              new Date(reminder.due_date) < new Date();
            const isCompleting = completingId === reminder.id;

            return (
              <div
                key={reminder.id}
                className={cn(
                  "rounded-xl border bg-card p-4 transition-colors",
                  isOverdue
                    ? "border-red-500/20"
                    : reminder.is_completed
                      ? "border-border opacity-70"
                      : "border-border"
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                          isOverdue
                            ? "bg-red-500/10"
                            : reminder.is_completed
                              ? "bg-green-500/10"
                              : "bg-secondary"
                        )}
                      >
                        <Bell
                          className={cn(
                            "h-4 w-4",
                            isOverdue
                              ? "text-red-400"
                              : reminder.is_completed
                                ? "text-green-400"
                                : "text-muted-foreground"
                          )}
                        />
                      </div>
                      <div>
                        <h3
                          className={cn(
                            "text-sm font-medium",
                            reminder.is_completed
                              ? "text-muted-foreground line-through"
                              : "text-foreground"
                          )}
                        >
                          {reminder.title}
                        </h3>
                        {reminder.description && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {reminder.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 pl-11 text-xs text-muted-foreground">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1",
                          isOverdue && "text-red-400"
                        )}
                      >
                        <CalendarDays className="h-3 w-3" />
                        {formatDateShort(reminder.due_date)}
                      </span>
                      {reminder.project_title && (
                        <button
                          onClick={() =>
                            navigate(`/projects/${reminder.project_id}`)
                          }
                          className="inline-flex items-center gap-1 transition-colors hover:text-accent"
                        >
                          <FolderKanban className="h-3 w-3" />
                          {reminder.project_title}
                        </button>
                      )}
                      {reminder.assigned_to_name && (
                        <span>{reminder.assigned_to_name}</span>
                      )}
                      <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5">
                        {REMINDER_TYPES[reminder.reminder_type] ||
                          reminder.reminder_type}
                      </span>
                    </div>
                  </div>
                  {!reminder.is_completed && (
                    <button
                      onClick={() => handleComplete(reminder.id)}
                      disabled={isCompleting}
                      className={cn(
                        "shrink-0 rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:border-green-500/30 hover:bg-green-500/10 hover:text-green-400",
                        isCompleting && "cursor-not-allowed opacity-50"
                      )}
                      title="Tamamlandı olarak işaretle"
                    >
                      {isCompleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Reminder Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowModal(false)}
          />
          <div className="relative z-10 w-full max-w-lg rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                Yeni Hatırlatıcı
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Başlık *
                </label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => handleFormChange("title", e.target.value)}
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Son Tarih *
                  </label>
                  <input
                    type="date"
                    required
                    value={form.due_date}
                    onChange={(e) =>
                      handleFormChange("due_date", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Tür
                  </label>
                  <select
                    value={form.reminder_type}
                    onChange={(e) =>
                      handleFormChange("reminder_type", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    {Object.entries(REMINDER_TYPES).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Proje
                </label>
                <select
                  value={form.project_id}
                  onChange={(e) =>
                    handleFormChange("project_id", e.target.value)
                  }
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">Proje seçin (opsiyonel)...</option>
                  {projectsData?.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Açıklama
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  rows={3}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                    submitting && "cursor-not-allowed opacity-50"
                  )}
                >
                  {submitting && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
